import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import DashboardLayout from "@/components/DashboardLayout";
import { MODULE_KEYS, PLANS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Users, Building2, ClipboardList, ShieldCheck, UserPlus, Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = { live_queue: "mod_live_queue", thermal_printing: "mod_thermal_printing", analytics: "mod_analytics", store: "mod_store" };

export default function Admin() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", mill_name: "", plan: "starter" });

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/plans").then((r) => setPlans(r.data));
  };
  useEffect(() => { load(); }, []);

  const setPlanPrice = (id, val) => setPlans((ps) => ps.map((p) => (p.plan_id === id ? { ...p, price: val } : p)));
  const savePlanPrice = async (p) => {
    try { await api.put(`/admin/plans/${p.plan_id}`, { price: parseFloat(p.price) || 0 }); toast.success(t("price_saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createUser = async () => {
    try { await api.post("/admin/users", form); toast.success(t("add_producer")); setOpen(false); setForm({ name: "", email: "", password: "", mill_name: "", plan: "starter" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleModule = async (u, mod) => {
    const modules = { ...u.modules, [mod]: !u.modules?.[mod] };
    await api.put(`/admin/users/${u.user_id}`, { modules });
    setUsers((us) => us.map((x) => (x.user_id === u.user_id ? { ...x, modules } : x)));
  };

  const toggleActive = async (u) => {
    await api.put(`/admin/users/${u.user_id}`, { active: !u.active });
    setUsers((us) => us.map((x) => (x.user_id === u.user_id ? { ...x, active: !u.active } : x)));
  };

  const changePlan = async (u, plan) => {
    await api.put(`/admin/users/${u.user_id}`, { plan });
    setUsers((us) => us.map((x) => (x.user_id === u.user_id ? { ...x, plan } : x)));
  };

  const remove = async (id) => { await api.delete(`/admin/users/${id}`); toast.success(t("delete")); load(); };

  const statCards = stats ? [
    { label: "total_users", value: stats.total_users, icon: Users, color: "#3B82F6" },
    { label: "producers_label", value: stats.producers, icon: Building2, color: "#3D522B" },
    { label: "platform_orders", value: stats.total_orders, icon: ClipboardList, color: "#D4A373" },
    { label: "platform_clients", value: stats.total_clients, icon: ShieldCheck, color: "#8B5CF6" },
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-page">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{t("admin_title")}</h1>
            <p className="mt-1 text-muted-foreground">{t("admin_sub")}</p>
          </div>
          <Button data-testid="add-producer-btn" onClick={() => setOpen(true)} className="rounded-full bg-primary hover:bg-olive-hover"><UserPlus className="mr-1.5 h-4 w-4" /> {t("add_producer")}</Button>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {statCards.map((c) => (
            <div key={c.label} data-testid={`admin-stat-${c.label}`} className="rounded-xl border border-border bg-card p-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${c.color}1a`, color: c.color }}><c.icon className="h-[18px] w-[18px]" /></div>
              <p className="mt-3 font-heading text-2xl font-bold">{c.value}</p>
              <p className="text-xs text-muted-foreground">{t(c.label)}</p>
            </div>
          ))}
        </div>

        {/* Plans & Pricing */}
        <div className="rounded-xl border border-border bg-card p-5" data-testid="admin-plans">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><CreditCard className="h-5 w-5 text-olive" /> {t("manage_pricing")}</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {plans.map((p) => (
              <div key={p.plan_id} className="rounded-lg border border-border p-4">
                <p className="font-heading font-semibold">{t(`plan_${p.plan_id}`)}</p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-muted-foreground">{p.currency === "EUR" ? "€" : "L"}</span>
                  <Input data-testid={`plan-price-${p.plan_id}`} type="number" step="0.5" value={p.price} onChange={(e) => setPlanPrice(p.plan_id, e.target.value)} className="h-9" />
                  <Button size="sm" data-testid={`save-plan-${p.plan_id}`} onClick={() => savePlanPrice(p)} className="shrink-0 bg-primary hover:bg-olive-hover">{t("save_price")}</Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Users table */}
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">{t("user_col")}</th>
                <th className="px-4 py-3">{t("plan_col")}</th>
                <th className="px-4 py-3">{t("modules_col")}</th>
                <th className="px-4 py-3">{t("status_col")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber text-sm font-bold text-olive">{u.name.charAt(0)}</div>
                      <div>
                        <p className="font-semibold">{u.name} {u.role === "admin" && <span className="ml-1 rounded bg-olive-light px-1.5 py-0.5 text-[10px] font-bold text-olive">{t("role_admin")}</span>}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                        <p className="text-[11px] text-muted-foreground">{u.client_count} clients · {u.order_count} orders</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={u.plan} onValueChange={(v) => changePlan(u, v)}>
                      <SelectTrigger data-testid={`plan-select-${u.user_id}`} className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PLANS.map((p) => <SelectItem key={p.id} value={p.id}>{t(`plan_${p.id}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                      {MODULE_KEYS.map((m) => (
                        <label key={m} className="flex items-center gap-1.5 text-[11px]" data-testid={`module-${m}-${u.user_id}`}>
                          <Switch checked={!!u.modules?.[m]} onCheckedChange={() => toggleModule(u, m)} className="scale-75" />
                          {t(MODULE_LABELS[m])}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2" data-testid={`active-toggle-${u.user_id}`}>
                      <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} />
                      <span className={`text-xs font-medium ${u.active ? "text-green-600" : "text-muted-foreground"}`}>{u.active ? t("active") : t("disabled")}</span>
                    </label>
                  </td>
                  <td className="px-4 py-3">
                    {u.role !== "admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`delete-user-${u.user_id}`} size="icon" variant="ghost" className="h-8 w-8 text-destructive"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>{t("delete")} {u.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently removes the account and all its data.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(u.user_id)} className="bg-destructive hover:bg-destructive/90">{t("delete")}</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-producer-dialog">
          <DialogHeader><DialogTitle>{t("add_producer")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{t("name")}</Label><Input data-testid="producer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("mill_name")}</Label><Input data-testid="producer-mill" value={form.mill_name} onChange={(e) => setForm({ ...form, mill_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("email")}</Label><Input data-testid="producer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("password")}</Label><Input data-testid="producer-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("plan_col")}</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger data-testid="producer-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{PLANS.map((p) => <SelectItem key={p.id} value={p.id}>{t(`plan_${p.id}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-producer-btn" onClick={createUser} disabled={!form.name || !form.email || !form.password} className="bg-primary hover:bg-olive-hover">{t("create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
